<script setup>
import { ref, onMounted } from 'vue';

const searchPhrase = ref('');
const total = ref(0);
const loadMoreCounter = ref(1);
const hasMore = ref(true);
const skip = ref(0);
const take = ref(8);
const searchActive = ref(false);
const navSearch = ref(null);
const searchTrigger = ref(null);
const navSearchField = ref(null);
const cancelSearch = ref(null);
const searching = ref(null);
const foundArticles = ref([]);

onMounted(() => {
    searchTrigger.value.addEventListener('click', () => {
        searchActive.value = !searchActive.value;

        if (searchActive.value) {
            document.body.classList.remove('js-scrolled');
        }
    });

    cancelSearch.value.addEventListener('click', () => {
        resetSearch();
    });

    navSearchField.value.addEventListener('keyup', e => {
        if (e.key === 'Escape') {
            resetSearch();
        }
    });
});

function resetSearch() {
    searchPhrase.value = '';
    searchActive.value = false;
    navSearchField.value.value = '';
    total.value = 0;
    skip.value = 0;
    foundArticles.value = [];
    searching.value = false;
}

function searchArticles(loadMore = false) {
    if (loadMore) {
        loadMoreCounter.value++;
    }

    searching.value = true;
    const newSearchPhrase = navSearchField.value.value;

    if (newSearchPhrase !== searchPhrase.value) {
        total.value = 0;
        skip.value = 0;
        foundArticles.value = [];
        hasMore.value = true;
        loadMoreCounter.value = 1;
    }

    searchPhrase.value = newSearchPhrase;

    if (searchPhrase.value.length === 0) {
        searching.value = false;
        return;
    }

    return fetch(`/umbraco/delivery/api/v2/content?filter=contentType:articlePage&skip=${skip.value}&take=${take.value}&sort=createDate:desc`)
        .then(response => response.json())
        .then(data => {
            total.value = data.total;

            foundArticles.value = foundArticles.value.concat(data.items.filter(article => (
                article.name.toLowerCase().includes(searchPhrase.value.toLowerCase()) ||
                article.properties.navigationName?.toLowerCase().includes(searchPhrase.value.toLowerCase()) ||
                article.properties.navigationTitle?.toLowerCase().includes(searchPhrase.value.toLowerCase()) ||
                article.properties.seoTitle?.toLowerCase().includes(searchPhrase.value.toLowerCase()) ||
                article.properties.seoDescription?.toLowerCase().includes(searchPhrase.value.toLowerCase()) ||
                article.properties.someTitle?.toLowerCase().includes(searchPhrase.value.toLowerCase()) ||
                article.properties.someDescription?.toLowerCase().includes(searchPhrase.value.toLowerCase()) ||
                article.properties.articleSettingsHeadline?.toLowerCase().includes(searchPhrase.value.toLowerCase()) ||
                article.properties.articleSettingsTeaserText?.toLowerCase().includes(searchPhrase.value.toLowerCase()) ||
                article.properties.articleSettingsGenreTag?.toLowerCase().includes(searchPhrase.value.toLowerCase()) ||
                article.properties.articleSettingsJournalist?.name?.toLowerCase().includes(searchPhrase.value.toLowerCase()) ||
                article.properties.moduleItems?.items?.filter(x =>
                    (x.content.contentType == 'text' || x.content.contentType == 'bulletList') &&
                    x.content.properties.richText?.markup.toLowerCase().includes(searchPhrase.value.toLowerCase())
                ).length > 0
            )));

            skip.value += take.value;

            if (
                (foundArticles.value.length < take.value && total.value > skip.value) ||
                foundArticles.value.length < (loadMoreCounter.value * take.value) && total.value > skip.value
            ) {
                searchArticles(false);
            } else {
                searching.value = false;
            }

            if (total.value <= skip.value) {
                hasMore.value = false;
            }
        });
}

function getImagePath(image) {
    if (!image) {
        return '';
    }

    return `${image.url}?rxy=${image.focalPoint?.top},${image.focalPoint?.left}&width=100&height=100&quality=90`;
}

function getDisplayDate(date) {
    return `${new Date(date).getDate()}/${new Date(date).getMonth() + 1} ${new Date(date).getFullYear()}`;
}
</script>
<template>
    <span ref="navSearch" :class="['nav__search', { 'nav__search--active': searchActive }]">
        <label ref="searchTrigger" for="navSearchField" class="nav__search-icon">
            <svg id="uuid-88af9be7-cdd2-4a57-8c7d-ab2fa4c08bf9" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25.86 25.86"><path d="m21.049,10.775c0,5.675-4.6,10.275-10.275,10.275S.5,16.449.5,10.775,5.1.5,10.775.5s10.275,4.6,10.275,10.275Zm-3.009,7.265l7.466,7.466-7.466-7.466Z" style="fill:none; stroke:#000; stroke-miterlimit:10;"/></svg>
        </label>
        <div class="nav__search-form-wrap  h--pad">
            <span class="nav__search-cancel" ref="cancelSearch"><span>←</span> Tilbage</span>
            <form class="nav__search-form" @submit.prevent="searchArticles(false)">
                <input id="navSearchField" ref="navSearchField" type="text" name="q" placeholder="Søg efter artikler på Kulturen.NU" />
                <button>Søg</button>
            </form>
        </div>
    </span>
    <div v-if="foundArticles.length > 0" class="nav__search-results">
        <a v-for="article in foundArticles" :key="article.id" :href="article.route.path" class="nav__search-result">
            <img v-if="article.properties.articleSettingsMainImage?.length > 0" :src="getImagePath(article.properties.articleSettingsMainImage[0])" alt="" />
            <div>
                <p>{{ getDisplayDate(article.properties.articleSettingsDate) }}: {{ article.properties.articleSettingsHeadline }}</p>
                <p>{{ article.properties.articleSettingsTeaserText?.substring(0, 100) }}<span v-if="article.properties.articleSettingsTeaserText?.length > 100">...</span></p>
            </div>
        </a>
        <span v-if="loadMoreCounter < 3" :class="['nav__search-results-load-more', { 'nav__search-results-load-more--disabled' : !hasMore }]" @click="(hasMore ? searchArticles(true) : null)">Vis flere resultater</span>
        <a v-else class="nav__search-results-all" :href="(`/soegeresultat?q=${searchPhrase}`)">Se alle resultater</a>
    </div>
</template>
